// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/css';
import {
  Box,
  Button,
  Divider,
  Paper,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { BiSolidUserPlus } from 'react-icons/bi';
import * as Sentry from '@sentry/react';

import { Users, Order_By } from '@gql/base-types';
import { useGetOrgUsersQuery } from '../queries/getOrgUsers.query.generated';
import DefaultErrorFallback from '@components/Fallbacks/DefaultErrorFallback';
import Spacer from '@components/Utils/Spacer';
import TeamTable from './TeamTable';
import TeamControls, { type TeamControlFilters } from './TeamControls';
import TeamMetrics, { type RoleMetric } from './TeamMetrics';
import BulkActionBar from './BulkActionBar';
import { buildScopedUsersWhere } from '../utils/whereClauseBuilders';
import { ROLE_DEALER_SUPER_ADMIN, ROLE_DEALER_ADMIN, ROLE_DEALER } from '../utils/roleUtils';
import { toast } from 'react-toastify';

// TYPES ---------------------------------------------------------------- //

interface TeamTabContentProps {
  customerIds: number[];
}

// COMPONENT ---------------------------------------------------------------- //

const TeamTabContent: React.FC<TeamTabContentProps> = ({ customerIds }) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const navigate = useNavigate();

  // Filter state
  const [filters, setFilters] = React.useState<TeamControlFilters>({
    features: [],
    status: null,
    search: '',
  });
  const [roleMetric, setRoleMetric] = React.useState<RoleMetric>('all');
  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(new Set());

  // Map role metric to a role filter for the query
  const roleFilter = React.useMemo(() => {
    switch (roleMetric) {
      case 'users': return [ROLE_DEALER, ROLE_DEALER_ADMIN];
      case 'admins': return [ROLE_DEALER_SUPER_ADMIN];
      default: return null;
    }
  }, [roleMetric]);

  const where = React.useMemo(
    () =>
      buildScopedUsersWhere(customerIds, {
        role: roleFilter ? undefined : null,
        showInactive: filters.status === 'inactive' || !filters.status,
        search: filters.search || undefined,
        features: filters.features.length > 0 ? filters.features : undefined,
      }),
    [customerIds, roleFilter, filters],
  );

  const { data, loading, error, refetch } = useGetOrgUsersQuery({
    variables: {
      where,
      orderBy: [{ display_name: Order_By.Asc }],
      limit: 500,
      offset: 0,
    },
    skip: customerIds.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const allUsers: Users[] = (data?.users as Users[]) ?? [];

  // Apply client-side filters: status + role metric
  const filteredUsers = React.useMemo(() => {
    let users = allUsers;

    // Status filter
    if (filters.status) {
      users = users.filter(u =>
        filters.status === 'active' ? u.active : !u.active,
      );
    }

    // Role metric filter
    if (roleFilter) {
      users = users.filter(u => roleFilter.includes(u.default_role));
    }

    return users;
  }, [allUsers, filters.status, roleFilter]);

  // Compute metric counts from the full (non-role-filtered) user list
  const metricCounts = React.useMemo(() => {
    const statusFiltered = filters.status
      ? allUsers.filter(u => filters.status === 'active' ? u.active : !u.active)
      : allUsers;

    return {
      all: statusFiltered.length,
      users: statusFiltered.filter(u =>
        u.default_role === ROLE_DEALER || u.default_role === ROLE_DEALER_ADMIN,
      ).length,
      admins: statusFiltered.filter(u =>
        u.default_role === ROLE_DEALER_SUPER_ADMIN,
      ).length,
    };
  }, [allUsers, filters.status]);

  // Bulk actions
  const handleGrantFeature = async (featureId: number) => {
    toast.info(`Bulk grant feature coming soon (${selectedIds.size} users, feature ${featureId})`);
  };

  const handleRevokeFeature = async (featureId: number) => {
    toast.info(`Bulk revoke feature coming soon (${selectedIds.size} users, feature ${featureId})`);
  };

  const handleDeactivate = () => {
    toast.info(`Bulk deactivation coming soon (${selectedIds.size} users)`);
  };

  if (error) {
    console.error('Error getting org users:', error);
    Sentry.captureException(error);
    return <DefaultErrorFallback message="ERROR DISPLAYING USERS" />;
  }

  return (
    <>
      <Paper className={cls.paper}>
        {/* Header area */}
        <div className={cls.header}>
          {/* Toolbar row */}
          <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
            <Typography variant="h6" fontWeight={600}>
              Users
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<BiSolidUserPlus />}
              onClick={() => navigate('/users/add')}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Add User
            </Button>
          </Box>

          {/* Controls row */}
          <Box px={2} pb={1}>
            <TeamControls filters={filters} onFiltersChange={setFilters} />
          </Box>

          <Spacer size={1} />

          {/* Metrics row */}
          <Box px={2} pb={2}>
            <TeamMetrics
              allCount={metricCounts.all}
              usersCount={metricCounts.users}
              adminsCount={metricCounts.admins}
              metric={roleMetric}
              onMetricChange={setRoleMetric}
              loading={loading && allUsers.length === 0}
            />
          </Box>
        </div>

        <Divider />

        {/* Table */}
        <Sentry.ErrorBoundary fallback={<DefaultErrorFallback message="ERROR DISPLAYING USERS TABLE" />}>
          <TeamTable
            users={filteredUsers}
            loading={loading}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            refetch={refetch}
          />
        </Sentry.ErrorBoundary>
      </Paper>

      {/* Empty state */}
      {!loading && allUsers.length === 0 && (
        <Paper className={cls.emptyState}>
          <Box className={cls.emptyIcon}>
            <BiSolidUserPlus size={40} color="#778088" />
          </Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            No users yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first user to start managing access and features.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BiSolidUserPlus />}
            onClick={() => navigate('/users/add')}
            sx={{ textTransform: 'none' }}
          >
            Add User
          </Button>
        </Paper>
      )}

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        onGrantFeature={handleGrantFeature}
        onRevokeFeature={handleRevokeFeature}
        onDeactivate={handleDeactivate}
      />
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  paper: css`
    border-radius: 12px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04);
    overflow: hidden;
  `,
  header: css`
    background-color: ${theme.palette.background.paper};
  `,
  emptyState: css`
    margin-top: ${theme.spacing(3)};
    padding: ${theme.spacing(6)};
    text-align: center;
    border-radius: 12px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04);
  `,
  emptyIcon: css`
    width: 96px;
    height: 96px;
    border-radius: 50%;
    background-color: #f1f2f3;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto ${theme.spacing(3)};
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default TeamTabContent;
